import React, { createContext, useContext, useState } from 'react';
import ApiService from '../services/api';
import { useAuth } from './AuthContext';

const UserFollowContext = createContext();

export const useUserFollow = () => {
  const context = useContext(UserFollowContext);
  if (!context) {
    throw new Error('useUserFollow must be used within a UserFollowProvider');
  }
  return context;
};

export const UserFollowProvider = ({ children }) => {
  const { user, updateUser } = useAuth();
  const [followStatuses, setFollowStatuses] = useState({}); // userId -> isFollowing

  // Check follow status for a user
  const checkFollowStatus = async (userId) => {
    try {
      const response = await ApiService.get(`/users/${userId}/follow-status`);
      if (response.success) {
        setFollowStatuses(prev => ({
          ...prev,
          [userId]: response.data.is_following
        }));
        return response.data;
      }
    } catch (err) {
      console.error('Error checking follow status:', err);
    }
    return { is_following: false };
  };

  // Follow a user
  const followUser = async (userId) => {
    try {
      const response = await ApiService.post(`/users/${userId}/follow`);
      if (response.success) {
        setFollowStatuses(prev => ({
          ...prev,
          [userId]: true
        }));
        // Update user's following count
        if (user && response.data.following_count !== undefined) {
          updateUser({ following_count: response.data.following_count });
        }
        return { success: true };
      }
      return { success: false, message: response.message };
    } catch (err) {
      const errorMsg = err.data?.message || err.message || 'Failed to follow user';
      return { success: false, message: errorMsg };
    }
  };

  // Unfollow a user
  const unfollowUser = async (userId) => {
    try {
      const response = await ApiService.delete(`/users/${userId}/follow`);
      if (response.success) {
        setFollowStatuses(prev => ({
          ...prev,
          [userId]: false
        }));
        // Update user's following count
        if (user && response.data.following_count !== undefined) {
          updateUser({ following_count: response.data.following_count });
        }
        return { success: true };
      }
      return { success: false, message: response.message };
    } catch (err) {
      const errorMsg = err.data?.message || err.message || 'Failed to unfollow user';
      return { success: false, message: errorMsg };
    }
  };

  // Get followers list
  const getFollowers = async (userId, page = 1) => {
    try {
      const response = await ApiService.get(`/users/${userId}/followers`, {
        params: { page, per_page: 20 }
      });
      if (response.success) {
        return { success: true, data: response.data };
      }
      return { success: false, message: response.message };
    } catch (err) {
      return { success: false, message: err.message };
    }
  };

  // Get following list
  const getFollowing = async (userId, page = 1) => {
    try {
      const response = await ApiService.get(`/users/${userId}/following`, {
        params: { page, per_page: 20 }
      });
      if (response.success) {
        return { success: true, data: response.data };
      }
      return { success: false, message: response.message };
    } catch (err) {
      return { success: false, message: err.message };
    }
  };

  const value = {
    followStatuses,
    checkFollowStatus,
    followUser,
    unfollowUser,
    getFollowers,
    getFollowing,
  };

  return (
    <UserFollowContext.Provider value={value}>
      {children}
    </UserFollowContext.Provider>
  );
};

